// LAS CITAS MEDICAS, DEL LADO DE LA PANTALLA (punto 10, fase 2).
//
// ── ESTAS LISTAS SON LAS MISMAS QUE LAS DEL SERVIDOR ────────────────────────
//
// `deskte-api/src/utils/citasMedicas.ts` lleva `ESTATUS_DE_CITA`,
// `CAMPOS_DE_CITA` y `LARGO_MAXIMO` con estos mismos valores, y
// `tests/unit/citasMedicas.test.ts` compara los dos ficheros. Un estatus que
// esta pantalla ofrezca y el servidor no acepte se rechaza con un 400 en
// ingles; un campo que mande y el servidor no recoja se guarda en el aire sin
// dar ningun error (la trampa de `pickData` del CLAUDE.md).

/** Los estatus, en el orden en que se ofrecen. */
export const ESTATUS_DE_CITA = [
  'pendiente',
  'confirmada',
  'completada',
  'no_asistio',
  'cancelada',
];

/** Como se ve cada estatus: etiqueta en los dos idiomas y su color. */
export const ESTATUS = {
  pendiente:  { es: 'Pendiente',   en: 'Pending',    icono: '🕐', color: '#ffb700' },
  confirmada: { es: 'Confirmada',  en: 'Confirmed',  icono: '✅', color: '#00d4ff' },
  completada: { es: 'Completada',  en: 'Completed',  icono: '🏁', color: '#00ff88' },
  no_asistio: { es: 'No asistió',  en: 'No-show',    icono: '🚫', color: '#ff3d6b' },
  cancelada:  { es: 'Cancelada',   en: 'Cancelled',  icono: '✖️', color: '#7aa3c0' },
};

export function etiquetaDeEstatus(id, lang = 'es') {
  const e = ESTATUS[id] || ESTATUS.pendiente;
  return lang === 'en' ? e.en : e.es;
}

export function colorDeEstatus(id) {
  return (ESTATUS[id] || ESTATUS.pendiente).color;
}

/** Los estatus en los que la cita ya no va a pasar. */
export const ESTATUS_CERRADOS = ['completada', 'no_asistio', 'cancelada'];

/** Los campos que se escriben, en el orden del formulario. */
export const CAMPOS_DE_CITA = [
  'orden',
  'razon',
  'proveedorNombre',
  'proveedorEspecialidad',
  'proveedorNpi',
  'proveedorTelefono',
  'dir1',
  'dir2',
  'ciudad',
  'estado',
  'zip',
  'condado',
  'notas',
];

/** Los mismos topes que el servidor. Se pasan al `maxLength` del campo. */
export const LARGO_MAXIMO = {
  orden: 60,
  razon: 2000,
  proveedorNombre: 160,
  proveedorEspecialidad: 80,
  proveedorNpi: 20,
  proveedorTelefono: 40,
  dir1: 200,
  dir2: 120,
  ciudad: 120,
  estado: 40,
  zip: 20,
  condado: 120,
  notas: 2000,
};

/**
 * Como se llama cada campo en pantalla.
 *
 * `ancho: 2` ocupa la fila entera; `area: true` va en un area de texto;
 * `soloSuelto: true` es de los que solo se piden cuando la cita NO cuelga de un
 * medico de la ficha (los datos salen del medico, no se vuelven a teclear).
 */
export const ETIQUETAS = {
  orden: {
    es: 'N.º de orden', en: 'Order no.',
    pistaEs: 'El que trae la orden del médico', pistaEn: 'From the referral order',
  },
  // La RAZON es el campo que de verdad se usa el dia de la cita: en las
  // capturas de Apizeal una razon real dice, en una frase, que chequeo es, que
  // medico atiende, de que numero se saco la cita, quien hizo de interprete y
  // cuantos minutos antes hay que llegar.
  razon: {
    es: 'Razón de la cita', en: 'Reason', ancho: 2, area: true,
    pistaEs: 'Chequeo anual con el Dr. ___; llegar 30 min antes; intérprete: ___',
    pistaEn: 'Annual check-up with Dr. ___; arrive 30 min early; interpreter: ___',
  },
  proveedorNombre: {
    es: 'Proveedor', en: 'Provider', ancho: 2, soloSuelto: true,
    pistaEs: 'Dr. Juan Pérez, MD', pistaEn: 'Dr. Jane Doe, MD',
  },
  proveedorEspecialidad: {
    es: 'Especialidad', en: 'Specialty', soloSuelto: true,
    pistaEs: 'PCP, Laboratorio…', pistaEn: 'PCP, Lab…',
  },
  proveedorNpi: { es: 'NPI', en: 'NPI', soloSuelto: true, pistaEs: '', pistaEn: '' },
  proveedorTelefono: {
    es: 'Teléfono del proveedor', en: 'Provider phone', ancho: 2, soloSuelto: true,
    pistaEs: '(954) 555-0123', pistaEn: '(954) 555-0123',
  },
  dir1: { es: 'Dirección', en: 'Address', ancho: 2, pistaEs: '', pistaEn: '' },
  dir2: { es: 'Apto / suite', en: 'Apt / suite', ancho: 2, pistaEs: '', pistaEn: '' },
  ciudad: { es: 'Ciudad', en: 'City', pistaEs: '', pistaEn: '' },
  estado: { es: 'Estado', en: 'State', pistaEs: 'FL', pistaEn: 'FL' },
  zip: { es: 'ZIP', en: 'ZIP', pistaEs: '', pistaEn: '' },
  // El CONDADO sale en la vista previa de Apizeal y no es un capricho: en
  // Florida decide a que red y a que plan pertenece el centro.
  condado: { es: 'Condado', en: 'County', pistaEs: 'Broward', pistaEn: 'Broward' },
  notas: { es: 'Notas', en: 'Notes', ancho: 2, area: true, pistaEs: '', pistaEn: '' },
};

/** Los campos que se piden cuando el proveedor NO es un médico de la ficha. */
export const CAMPOS_DEL_PROVEEDOR_SUELTO = CAMPOS_DE_CITA.filter((c) => ETIQUETAS[c]?.soloSuelto);

/**
 * ── EL RECORDATORIO AL CLIENTE (fase 3) ─────────────────────────────────────
 *
 * Lissette, 2026-09-16: *"la opción de recordatorio de 10 días antes y 1 día
 * antes, o los días que uno coloque, y que le llegue por correo"*.
 *
 * Los tres de un clic son los suyos más uno intermedio; se puede escribir
 * cualquier otro. VACÍO de fábrica: esto manda correos a clientes, y nada sale
 * si nadie lo pide en esa cita.
 *
 * Espejo de `deskte-api/src/utils/recordatorioDeCita.ts`, que es quien manda.
 */
export const DIAS_SUGERIDOS = [10, 3, 1];
export const MAXIMO_DE_AVISOS = 5;
export const DIAS_MAXIMO = 365;

/** La misma limpieza que el servidor, para que la pantalla no prometa de más. */
export function normalizarDias(entrada) {
  const bruto = Array.isArray(entrada) ? entrada : [];
  const vistos = new Set();
  for (const x of bruto) {
    const n = Math.trunc(Number(x));
    if (!Number.isFinite(n) || n < 1 || n > DIAS_MAXIMO) continue;
    vistos.add(n);
  }
  return [...vistos].sort((a, b) => b - a).slice(0, MAXIMO_DE_AVISOS);
}

/** "10 días antes" / "1 día antes". */
export function etiquetaDeAviso(dias, lang = 'es') {
  const n = Number(dias);
  if (lang === 'en') return n === 1 ? '1 day before' : `${n} days before`;
  return n === 1 ? '1 día antes' : `${n} días antes`;
}

/** El formulario vacío. Todas las claves puestas a "" (nunca `undefined`). */
export function citaVacia(persona = 'primary', cuando = '') {
  // `esNueva` + `avisarAhora`: al CREAR se ofrece mandarle al cliente la
  // confirmación en ese momento, marcada de entrada (Lissette 2026-10-23). Al
  // editar no se ofrece: la cita ya existe.
  const o = { persona, doctorId: '', startsAt: cuando, status: 'pendiente', enElCalendario: false, reminderDays: [], esNueva: true, avisarAhora: true };
  for (const c of CAMPOS_DE_CITA) o[c] = '';
  return o;
}

/** Una cita que vino del servidor, como formulario. */
export function citaComoFormulario(cita) {
  const o = citaVacia((cita && cita.persona) || 'primary');
  for (const c of CAMPOS_DE_CITA) o[c] = (cita && cita[c]) || '';
  o.doctorId = (cita && cita.doctorId) || '';
  // Quién atiende, tal como lo enseña la tarjeta (el médico VIVO de la ficha).
  // Solo para MOSTRARLO en el formulario: no se manda al guardar.
  o.proveedorDeLaCita = (cita && cita.proveedor) || null;
  o.status = (cita && cita.status) || 'pendiente';
  o.enElCalendario = !!(cita && cita.enElCalendario);
  o.reminderDays = normalizarDias(cita && cita.reminderDays);
  o.startsAt = paraElCampoDeFecha(cita && cita.startsAt);
  o.esNueva = false;
  o.avisarAhora = false;
  return o;
}

/**
 * Qué decir de la confirmación al cliente después de guardar. null = no se
 * pidió, o salió bien (eso ya lo dice "Cita guardada"). El servidor devuelve
 * `{ enviado, motivo }` (routes/medical-appointments.routes.ts#confirmarAlCliente).
 */
export function textoDeLaConfirmacion(confirmacion, es = true) {
  if (!confirmacion || confirmacion.enviado) return null;
  const m = confirmacion.motivo;
  if (m === 'sin_correo') return es ? 'La cita se guardó, pero el cliente no tiene correo en su ficha: no se le envió la confirmación.' : 'Saved, but the client has no email on file: no confirmation was sent.';
  if (m === 'sin_buzon') return es ? 'La cita se guardó, pero no se envió la confirmación: conecta tu correo en Mi Perfil.' : 'Saved, but no confirmation was sent: connect your email in My Profile.';
  if (m === 'no_supervisor') return es ? 'La cita se guardó, pero no se envió la confirmación: este asiento no tiene agente asignado.' : 'Saved, but no confirmation was sent: this seat has no supervising agent.';
  if (m === 'pasada') return es ? 'La cita se guardó. No se envió confirmación porque la fecha ya pasó.' : 'Saved. No confirmation was sent because the date has passed.';
  if (m === 'cerrada') return es ? 'La cita se guardó. No se envió confirmación porque está cerrada (completada, cancelada o no asistió).' : 'Saved. No confirmation was sent because it is closed.';
  return es ? 'La cita se guardó, pero no se pudo enviar la confirmación al cliente. Intenta de nuevo más tarde.' : 'Saved, but the confirmation could not be sent to the client.';
}

/**
 * Un instante ISO -> el valor de un `<input type="datetime-local">`.
 *
 * Se usan las partes LOCALES, nunca `toISOString()`: en Florida ese atajo
 * adelanta la cita cuatro o cinco horas, y una cita de las 8 de la noche pasa a
 * ser del dia siguiente.
 */
export function paraElCampoDeFecha(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Cómo se lee la fecha y la hora de una cita. Siempre en-US, y en local. */
export function cuandoEs(iso, lang = 'es') {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dia = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const hora = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return lang === 'en' ? `${dia} · ${hora}` : `${dia} · ${hora}`;
}

/**
 * "Faltan 3 días", "Fue hace 2 semanas", "Hoy".
 *
 * Se compara por DIA LOCAL, no por horas: una cita de mañana a las 8 am está a
 * menos de 24 h y aun así es "mañana", no "hoy".
 */
export function cuantoFalta(iso, lang = 'es') {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const es = lang !== 'en';
  const hoy = new Date();
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const b = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const dias = Math.round((a - b) / 86400000);
  if (dias === 0) return es ? 'Hoy' : 'Today';
  if (dias === 1) return es ? 'Mañana' : 'Tomorrow';
  if (dias === -1) return es ? 'Ayer' : 'Yesterday';
  if (dias > 0) return es ? `En ${dias} días` : `In ${dias} days`;
  return es ? `Hace ${-dias} días` : `${-dias} days ago`;
}
